import React from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import LazyImage from "../LazyImage";
import type { Transfer } from "../../hooks/useTransfers";
import { transferPlayerPath } from "../../utils/transferPaths";

interface Props {
  transfer: Transfer;
  rtl: boolean;
}

const POSITION_LABEL: Record<string, string> = {
  G: "GK",
  D: "DEF",
  M: "MID",
  A: "ATT",
};

function formatRelative(ts: number, locale: string): string {
  if (!ts) return "";
  const date = new Date(ts * 1000);
  try {
    return new Intl.DateTimeFormat(locale === "fa" ? "fa-IR" : locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);
  } catch {
    return date.toLocaleDateString();
  }
}

function DirectionArrow({ rtl }: { rtl: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={`h-5 w-5 text-[#1b4d3e] ${rtl ? "rotate-180" : ""}`}
      aria-hidden
    >
      <path
        d="M5 12h13M13 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TeamBlock({
  team,
  label,
  align,
}: {
  team: Transfer["fromTeam"];
  label: string;
  align: "start" | "end";
}) {
  const className = `flex flex-1 flex-col items-${align} text-center min-w-0`;

  if (!team) {
    const emptyTitle = label === "FROM" ? "بازیکن آزاد" : "—";
    return (
      <div className={className}>
        <div className="mx-auto mb-1.5 flex h-12 w-12 items-center justify-center rounded-full border border-[#e6e4df] bg-[#f6f5f2]">
          <span className="text-[10px] text-[#8b919c]">{label === "FROM" ? "آزاد" : "?"}</span>
        </div>
        <span className="text-[10px] uppercase tracking-wide text-[#8b919c]">{label}</span>
        <span className="text-xs font-medium text-[#5c6370]">{emptyTitle}</span>
      </div>
    );
  }

  const body = (
    <>
      <div className="relative mx-auto mb-1.5 flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border border-[#e6e4df] bg-[#fafaf8]">
        {team.logo ? (
          <LazyImage
            src={team.logo}
            alt={team.title}
            className="h-8 w-8 object-contain"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <span className="text-xs text-[#8b919c]">{team.title?.[0]}</span>
        )}
      </div>
      <span className="text-[10px] uppercase tracking-wide text-[#8b919c]">{label}</span>
      <span className="max-w-[110px] truncate text-xs font-semibold text-[#1a1c1e]" title={team.title}>
        {team.title}
      </span>
    </>
  );

  if (team.externalId) {
    return (
      <Link
        to={`/transfers/team/${team.externalId}`}
        onClick={(e) => e.stopPropagation()}
        className={`${className} transition-opacity hover:opacity-80`}
      >
        {body}
      </Link>
    );
  }

  return <div className={className}>{body}</div>;
}

const TransferCard: React.FC<Props> = ({ transfer, rtl }) => {
  const navigate = useNavigate();
  const posLabel = POSITION_LABEL[transfer.player.positionKey] || transfer.player.positionKey || "—";

  return (
    <motion.div
      role="link"
      tabIndex={0}
      onClick={() => navigate(transferPlayerPath(transfer.player))}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate(transferPlayerPath(transfer.player));
        }
      }}
      whileHover={{ y: -1 }}
      transition={{ duration: 0.2 }}
      className="group relative w-full cursor-pointer rounded-xl border border-[#e6e4df] bg-white p-4 text-start transition-colors hover:border-[#c9c6bf] hover:bg-[#fbfbfa] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1b4d3e]/35"
    >
      <div className="mb-3.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="inline-flex items-center rounded-md border border-[#e6e4df] bg-[#f6f5f2] px-2 py-0.5 text-[10px] font-semibold text-[#3d4450]">
            {transfer.transferTitle || transfer.transferType}
          </span>
          {transfer.isImportant && (
            <span className="inline-flex items-center rounded-md bg-[#1b4d3e] px-2 py-0.5 text-[10px] font-semibold tracking-wide text-white">
              ویژه
            </span>
          )}
        </div>
        <span className="shrink-0 text-[11px] text-[#8b919c]">
          {formatRelative(transfer.transferTime, rtl ? "fa" : "en")}
        </span>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-full border border-[#e6e4df] bg-[#f6f5f2]">
          {transfer.player.image ? (
            <LazyImage
              src={transfer.player.image}
              alt={transfer.player.fullname}
              className="h-full w-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-lg font-semibold text-[#8b919c]">
              {transfer.player.fullname?.[0] || "?"}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="truncate text-[15px] font-semibold text-[#1a1c1e]" dir="auto">
              {transfer.player.fullname}
            </h3>
            {posLabel && posLabel !== "—" && (
              <span className="shrink-0 rounded border border-[#e6e4df] bg-[#f6f5f2] px-1.5 py-0.5 text-[10px] font-medium tracking-wide text-[#5c6370]">
                {posLabel}
              </span>
            )}
          </div>
          {transfer.player.englishName &&
            transfer.player.englishName !== transfer.player.fullname && (
              <p className="truncate text-xs text-[#8b919c]" dir="ltr">
                {transfer.player.englishName}
              </p>
            )}
          {(transfer.player.positionValue || transfer.player.kitNumber) && (
            <p className="mt-0.5 text-[11px] text-[#5c6370]">
              {[transfer.player.positionValue, transfer.player.kitNumber ? `#${transfer.player.kitNumber}` : ""]
                .filter(Boolean)
                .join(" · ")}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <TeamBlock team={transfer.fromTeam} label="FROM" align="start" />
        <div className="flex flex-col items-center px-1 mb-10">
          <DirectionArrow rtl={rtl} />
          {transfer.fullTransferFee && (
            <span className="mt-1 inline-flex max-w-[110px] truncate rounded-md bg-[#e8f0ec] px-2 py-0.5 text-[10px] font-semibold text-[#1b4d3e]">
              {transfer.fullTransferFee}
            </span>
          )}
        </div>
        <TeamBlock team={transfer.toTeam} label="TO" align="end" />
      </div>

      {(transfer.fromCompetition || transfer.toCompetition) && (
        <div className="mt-3.5 flex items-center justify-between gap-2 border-t border-[#eeebe6] pt-3 text-[10px] text-[#8b919c]">
          {transfer.fromCompetition ? (
            <div className="flex min-w-0 items-center gap-1.5">
              {transfer.fromCompetition.logo && (
                <img
                  src={transfer.fromCompetition.logo}
                  alt=""
                  className="h-3.5 w-3.5 object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              )}
              <span className="max-w-[120px] truncate">{transfer.fromCompetition.title}</span>
            </div>
          ) : (
            <span />
          )}
          {transfer.toCompetition ? (
            <div className="flex min-w-0 items-center gap-1.5">
              <span className="max-w-[120px] truncate text-end">{transfer.toCompetition.title}</span>
              {transfer.toCompetition.logo && (
                <img
                  src={transfer.toCompetition.logo}
                  alt=""
                  className="h-3.5 w-3.5 object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              )}
            </div>
          ) : (
            <span />
          )}
        </div>
      )}
    </motion.div>
  );
};

export default TransferCard;
